
import EmptyList from "@/app/ui/components/empty-list/index.vue";
import ErrorMessage from "@/app/ui/components/error-message/index.vue";
import Icons from "@/app/ui/components/icons/index.vue";
import TableLazyLoad from "@/app/ui/components/lazy-load/table-lazy-load.vue";
import PaginationV3 from "@/app/ui/components/pagination-v3/index.vue";
import { PickupDashboardController } from "@/app/ui/controllers/PickupDashboardController";
import { Pagination } from "@/domain/entities/Pagination";
import { RequestListSummary } from "@/domain/entities/PickupDashboardSummary";
import type { CSSProperties, PropType } from "vue";
import { computed, defineComponent, ref } from "vue";

interface TableColumn {
  key: string;
  label: string;
  sortable?: boolean;
  style?: CSSProperties | Record<string, any>;
}

export default defineComponent({
  name: "TableTransferArea",

  components: {
    PaginationV3,
    TableLazyLoad,
    EmptyList,
    ErrorMessage
  },

  props: {
    tableData: {
      type: Array as PropType<Array<any>>,
      required: true,
    },
   pagination: {
      type: Object as PropType<Pagination>,
      default: () => new Pagination(1, 10, 0),
    },
    startIndex: {
      type: Number,
      default: 0,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    enableSort: {
      type: Boolean,
      default: false
    },
    columns: {
      type: Array as PropType<TableColumn[]>,
      required: true,
      default: () => [],
    },
    sortKey: {
      type: String as PropType<string | null>,
      default: null,
    },
    sortOrder: {
      type: String as PropType<"asc" | "desc" | null>,
      default: null,
    },
    substractHeight: {
      type: String as PropType<string>,
      default: "500",
    },
    errorCause: {
      type: String as PropType<string | null>,
      default: null,
    }
  },

  emits: ["sort", "set-page", "select-all", "select-row", "try-again"],

  setup(props, {emit}) {
    const selectedRows = ref<any[]>([]);


    const onSort = (col: TableColumn) => {
      if (!props.enableSort || col.sortable === undefined) return;

      let nextOrder: "asc" | "desc" = "desc";

      if (props.sortKey === col.key) {
        nextOrder = props.sortOrder === "desc" ? "asc" : "desc";
      }

      emit("sort", {
        key: col.key,
        order: nextOrder,
      });
    };

    const isSelected = (item: any) =>
      selectedRows.value.some((v: any) => v.id === item.id);

    const selectableRows = computed(() => {
      return props.tableData
    });

    const isAllSelected = computed(() => {
      const rows = selectableRows.value;
      return rows.length > 0 && rows.every(item => isSelected(item));
    });

    const selectedIds = computed<number[]>(() =>
      selectedRows.value.map(row => row.id)
    );


    const toggleSelectAll = () => {
      const rows = selectableRows.value;
      if (!rows.length) return;

      if (isAllSelected.value) {
        const ids = rows.map(i => i.id);
        selectedRows.value = selectedRows.value.filter(
          v => !ids.includes(v.id)
        );
      } else {
        selectedRows.value = Array.from(
          new Map(
            [...selectedRows.value, ...rows].map(i => [i.id, i])
          ).values()
        );
      }

      emit("select-all", selectedIds.value);
    };

    const toggleRow = (item: any) => {
      const exists = isSelected(item);

      if (exists) {
        selectedRows.value = selectedRows.value.filter(
          v => v.id !== item.id
        );
      } else {
        selectedRows.value = [...selectedRows.value, item];
      }

      emit("select-row", selectedIds.value);
    };


    const onSetPage = (page: number) => {
      emit("set-page", page)
    }

    const getCellValue = (item: any, key: string) => {
      return (item as any)[key];
    };

    const isFiltering = computed(() => {
      const curentFilter = PickupDashboardController.currentFilter;
      return (
        !!curentFilter.q ||
        !!curentFilter.shipmentType ||
        !!curentFilter.startDate ||
        !!curentFilter.endDate
      );
    });

    return {
      selectedRows,
      isAllSelected,
      toggleSelectAll,

      onSort,
      toggleRow,
      onSetPage,
      getCellValue,
      isSelected,
      isFiltering,
    };
  },
});
